'use client';
import { config } from '@/apollo/config';
import IconArrowMap from '@/assets/icons/order/icon-map.svg';
import { CustomSelect } from '@/components';
import Input from '@/components/ui/input';
import {
  useCityShippingMethodsListQuery,
  useRegionalShippingMethodsQuery,
  UserOrdersQuery,
} from '@/graphql';
import { Address } from '@/graphql/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { useCityStore } from '@/zustand/cityStore';
import useUserStore from '@/zustand/useUser';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/uz-latn';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { useWindowSize } from 'react-use';
import { Button, SelectPicker } from 'rsuite';
import styles from './delivery.module.scss';
import MapModal from './modal/mapModal';

type Props = {
  onCheckedChange: (checked: boolean) => void;
  orderData: UserOrdersQuery | undefined;
  checked: boolean;
  onClickPaymentsTab: () => void;
  shippingId: string | null;
  setShippingId: (item: string | null) => void;
  setAddressFormData: (item: Address | null) => void;
  setShippingDate: (date: string | null) => void;
  shippingDate: string | null;
  setShippingStartTime: (time: string) => void;
  shippingStartTime: string;
  setShippingEndTime: (time: string) => void;
  setHasCities: (city: boolean) => void;
  hasCities: boolean;
  setDeliveryPrice: (price: null | number) => void;
};

const YandexDeliverySection = (props: Props) => {
  const { user } = useUserStore();
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const { width } = useWindowSize();
  const [btnState, setBtnState] = useState(true);
  const [mapModal, setMapModal] = useState(false);
  const t = useTranslations('Components.delivery');
  const locale = useLocale();
  dayjs.locale(locale === 'uz' ? 'uz-latn' : 'ru');

  const { cityValue } = useCityStore();
  const order = props.orderData?.userDraftOrders?.[0];
  const defaultShippingAddress = order?.user?.defaultShippingAddress;

  const addresses = useMemo(() => {
    return (order?.user?.addresses || []) as Address[];
  }, [order?.user?.addresses]);

  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const defaultAddress = useMemo(() => {
    if (selectedAddress) {
      return (
        addresses.find(addr => addr.id === selectedAddress) || ({} as Address)
      );
    } else {
      return {} as Address;
    }
  }, [selectedAddress, addresses]);
  const { data: cityShippingData, loading: cityShippingLoading } =
    useCityShippingMethodsListQuery({
      variables: {
        first: 1,
        filter: {
          city: cityValue,
        },
      },
      skip: !user,
    });

  const { data: regionalShippingData, loading: regionalShippingLoading } =
    useRegionalShippingMethodsQuery({
      variables: {
        first: 100,
      },
    });

  useEffect(() => {
    if (
      cityShippingData &&
      cityShippingData.cityShippingMethods &&
      cityShippingData.cityShippingMethods.edges &&
      cityShippingData.cityShippingMethods.edges.length > 0
    ) {
      props.setHasCities(true);
    } else {
      props.setHasCities(false);
    }
    props.setDeliveryPrice(
      cityShippingData?.cityShippingMethods?.edges[0]?.node
        ?.yandexDeliveryPrice ?? null
    );
  }, [cityShippingData]);

  const cityShippingMethod =
    cityShippingData?.cityShippingMethods?.edges[0]?.node;

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
    trigger,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: defaultAddress?.name || '',
      streetAddress: defaultAddress?.streetAddress || '',
      houseNumber: defaultAddress?.houseNumber || '',
      apartmentNumber: defaultAddress?.apartmentNumber || '',
      houseSection: defaultAddress?.houseSection || '',
      floor: defaultAddress?.floor || '',
      doorCode: defaultAddress?.doorCode || '',
      note: defaultAddress?.note || '',
    },
    criteriaMode: 'all',
  });

  useEffect(() => {
    if (defaultShippingAddress) {
      setSelectedAddress(defaultShippingAddress.id || null);
    }
  }, [defaultShippingAddress]);

  useEffect(() => {
    reset({
      name: defaultAddress?.name || '',
      streetAddress: defaultAddress?.streetAddress || '',
      houseNumber: defaultAddress?.houseNumber || '',
      apartmentNumber: defaultAddress?.apartmentNumber || '',
      houseSection: defaultAddress?.houseSection || '',
      floor: defaultAddress?.floor || '',
      doorCode: defaultAddress?.doorCode || '',
      note: defaultAddress?.note || '',
    });
  }, [selectedAddress, reset, addresses, defaultShippingAddress]);

  const addressData = addresses.map(address => ({
    label: address.name,
    value: address.id,
  }));

  const handleAddressChange = (value: string | null) => {
    setSelectedAddress(value);
  };

  const handleSelectedDate = (item: any) => {
    if (!item.disabled) {
      setSelectedDate(item.id);
      const today = dayjs();
      const deliveryDate = today.add(item.id - 1, 'day');
      props.setShippingDate(deliveryDate.format('YYYY-MM-DD'));
    }
  };
  const handleSelectDelivery = (id: any) => {
    props.setShippingId(id);
    // props.onCheckedChange(true)
  };

  const [date, setDate] = useState<Date | null>(null);
  const handleDateSelect = (selectedDate: Date | null) => {
    setDate(selectedDate);
  };

  const handlePaymentsTabClick = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmit((data: any) => {
        props.onClickPaymentsTab();
        props.setAddressFormData(data as Address);
      })();
    }
  };

  const generateDeliveryOptions = () => {
    const today = dayjs();
    const weekendDays = cityShippingMethod?.weekendDays || [];
    const options = [];
    for (let i = 0; i < 7; i++) {
      const deliveryDate = today.add(i, 'day');
      const dayOfWeek = deliveryDate.day();
      console.log('deliveryDate', deliveryDate.locale('uz').format('D MMMM'));

      const isWeekend = weekendDays.includes(dayOfWeek);

      let dateLabel;
      if (i === 0) {
        dateLabel = t('today');
      } else if (i === 1) {
        dateLabel = t('tomorrow');
      } else {
        dateLabel = deliveryDate.locale('uz').format('D MMMM');
      }

      options.push({
        id: i + 1,
        date: dateLabel,
        price: cityShippingMethod
          ? cityShippingMethod.yandexDeliveryPrice === 0
            ? t('free')
            : t('start-price', {
                price: convertToReadable(
                  cityShippingMethod.yandexDeliveryPrice
                ),
              })
          : t('clarify'),
        disabled: isWeekend,
      });
    }

    return options;
  };

  const handleMapAddressSelect = (data: Address) => {
    setValue('streetAddress', data.streetAddress ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('houseNumber', data.houseNumber ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('apartmentNumber', data.apartmentNumber ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('houseSection', data.houseSection ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('floor', data.floor ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('doorCode', data.doorCode ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('note', data.note ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setMapModal(false);
  };

  const deliveryOptions = generateDeliveryOptions();

  useEffect(() => {
    if (!order?.deliveryMethod) {
      props.setShippingId(null);
      props.setShippingDate('');
      setBtnState(false);
      setSelectedDate(null);
    }
  }, [props.hasCities]);

  useEffect(() => {
    props.onCheckedChange(isValid);
  }, [isValid, props.onCheckedChange]);

  useEffect(() => {
    if (cityShippingMethod) {
      props.setShippingId(cityShippingMethod?.id);
    }
  }, [cityShippingMethod?.id, props.setShippingId]);

  useEffect(() => {
    setBtnState(!props.checked || props.shippingId === null);
  }, [props.checked, props.shippingId]);

  useEffect(() => {
    setBtnState(
      !props.checked ||
        props.shippingDate === '' ||
        props.shippingStartTime === ''
    );
  }, [props.checked, props.shippingDate, props.shippingStartTime]);

  useEffect(() => {
    if (
      cityShippingData &&
      cityShippingData.cityShippingMethods &&
      cityShippingData.cityShippingMethods.edges &&
      cityShippingData.cityShippingMethods.edges.length > 0 &&
      cityShippingData.cityShippingMethods.edges[0].node &&
      cityShippingData.cityShippingMethods.edges[0].node.id
    ) {
      const newShippingId =
        cityShippingData.cityShippingMethods.edges[0].node.id;
      if (props.shippingId !== newShippingId) {
        props.setShippingId(newShippingId);
      }
    }
  }, [cityShippingData, props.shippingId, props.setShippingId]);

  useEffect(() => {
    if (order?.shippingDate) {
      try {
        const today = dayjs().startOf('day');

        const shippingDateParsed = dayjs(order.shippingDate).startOf('day');

        if (!shippingDateParsed.isValid()) {
          return;
        }

        const differenceInDays = shippingDateParsed.diff(today, 'day');

        const targetId = differenceInDays + 1;

        if (targetId >= 1 && targetId <= 7) {
          const deliveryDateForCheck = today.add(differenceInDays, 'day');
          const dayOfWeek = deliveryDateForCheck.day();
          const weekendDays = cityShippingMethod?.weekendDays || [];
          const isWeekend = weekendDays.includes(dayOfWeek);

          if (!isWeekend) {
            setSelectedDate(targetId);
            const today = dayjs();
            const deliveryDate = today.add(targetId - 1, 'day');
            props.setShippingDate(deliveryDate.format('YYYY-MM-DD'));
          }
        }
      } catch (error) {
        toast.error(`Ошибка при обработке shippingDate: ${error}`);
      }
    }
  }, [order, setSelectedDate, cityShippingMethod]);

  return (
    <>
      <div className={styles.deliveriesWrapper}>
        <div className={styles.form}>
          <div className={styles.address}>
            <div className={styles.addressTitle}>{t('address-delivery')}</div>
            <div className={styles.addressSelect}>
              {/* Мои адреса <IconArrowDown /> */}
              <SelectPicker
                data={addressData}
                searchable={false}
                placeholder={t('my-address')}
                appearance="subtle"
                onChange={handleAddressChange}
                value={selectedAddress || null}
              />
            </div>
          </div>
          <form>
            <Controller
              name="streetAddress"
              control={control}
              rules={{ required: 'Адрес обязателен' }}
              render={({ field }) => (
                <Input
                  state={field.value}
                  setState={field.onChange}
                  label={t('address')}
                  error={errors}
                  name="streetAddress"
                  className={styles.inputMap}
                >
                  <Button
                    type="button"
                    className={styles.btn}
                    onClick={() => setMapModal(true)}
                  >
                    <IconArrowMap />
                    {width > 576 && <span>{t('on-map')}</span>}
                  </Button>
                </Input>
              )}
            />
            <div className={styles.inputGroup}>
              <Controller
                name="houseNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('home')}
                    error={errors}
                    name="houseNumber"
                    className={styles.inputMap}
                  />
                )}
              />
              <Controller
                name="apartmentNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('kv')}
                    error={errors}
                    name="apartmentNumber"
                    className={styles.inputMap}
                  />
                )}
              />
              <Controller
                name="houseSection"
                control={control}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('entrance')}
                    error={errors}
                    name="houseSection"
                    className={styles.inputMap}
                  />
                )}
              />
              <Controller
                name="floor"
                control={control}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('floor')}
                    error={errors}
                    name="floor"
                    className={styles.inputMap}
                  />
                )}
              />
              <Controller
                name="doorCode"
                control={control}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('intercom')}
                    error={errors}
                    name="doorCode"
                    className={styles.inputMap}
                  />
                )}
              />
            </div>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <Input
                  state={field.value}
                  setState={field.onChange}
                  label={t('comment')}
                  error={errors}
                  name="note"
                  className={styles.comment}
                />
              )}
            />
            {props.hasCities && (
              <div className={styles.deliveryWrapper}>
                <h4 className={styles.deliveryTitle}>
                  {t('date-and-time-delivery')}
                </h4>
                <div className={styles.deliveryDate}>
                  <div className={styles.dateTitle}>{t('date-delivery')}</div>
                  <div className={styles.cardsWrapper}>
                    {deliveryOptions.map(item => (
                      <div
                        key={item.id}
                        className={`${styles.card} ${
                          selectedDate === item.id && styles.activeCard
                        } ${item.disabled && styles.disabled}`}
                        onClick={() => handleSelectedDate(item)}
                      >
                        <div className={styles.day}>{item.date}</div>
                        <div className={styles.price}>{item.price}</div>
                      </div>
                    ))}
                    {/* <CustomCalendar
                                            onDateSelect={handleDateSelect}
                                            weekendDays={cityShippingMethod?.weekendDays}
                                        /> */}
                  </div>
                </div>
                <div className={styles.timeDelivery}>
                  {/* <EditableTime /> */}
                  <div className={styles.timeDeliveryWrapper}>
                    <p className={styles.timeDeliveryTitle}>
                      {t('time-delivery')}
                    </p>
                    <CustomSelect
                      time={cityShippingData}
                      setShippingStartTime={props.setShippingStartTime}
                      setShippingEndTime={props.setShippingEndTime}
                      shippingEndTime={order?.shippingEndTime}
                      shippingStartTime={order?.shippingStartTime}
                      shippingDate={
                        order?.shippingDate
                          ? order?.shippingDate
                          : props.shippingDate
                      }
                      cityShippingMethod={cityShippingMethod?.price}
                    />
                  </div>
                  <div>
                    {cityShippingMethod
                      ? cityShippingMethod.yandexDeliveryPrice === 0
                        ? t('free')
                        : t('start-price', {
                            price: convertToReadable(
                              cityShippingMethod.yandexDeliveryPrice
                            ),
                          })
                      : t('clarify')}
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
        {!props.hasCities && (
          <div className={styles.typeDelivery}>
            <h4>{t('type-delivery')}</h4>
            <div className={styles.typeCards}>
              {regionalShippingLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <div key={index}>
                      <Skeleton
                        className={styles.skeleton}
                        width={140}
                        height={72}
                      />
                    </div>
                  ))
                : regionalShippingData?.regionalShippingMethods?.edges.map(
                    item => {
                      return (
                        <div
                          key={item?.node?.id}
                          onClick={() => handleSelectDelivery(item?.node?.id)}
                          className={`${styles.card} ${
                            props.shippingId === item?.node?.id &&
                            styles.cardActive
                          }`}
                        >
                          {item?.node?.image && (
                            <Image
                              src={`${config?.publicApiUrl}${item?.node?.image}`}
                              width={90}
                              height={40}
                              alt="typeDelivery"
                              style={{ objectFit: 'contain' }}
                            />
                          )}
                        </div>
                      );
                    }
                  )}
            </div>
            <p>{t('delivery-price-text')}</p>
          </div>
        )}
      </div>
      <div className={styles.bottom}>
        <Button
          type="button"
          disabled={btnState}
          onClick={handlePaymentsTabClick}
          className={btnState ? styles.disabledBtn : styles.paymentBtn}
        >
          {t('switching-method-payment')}
        </Button>
      </div>
      {mapModal && (
        <MapModal
          open={mapModal}
          close={() => setMapModal(false)}
          onSelectAddress={handleMapAddressSelect}
        />
      )}
    </>
  );
};

export default YandexDeliverySection;
